import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Table, Button, Select, Space, DatePicker, Tag, message, Input, InputNumber,
} from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import ResizableTitle from '../components/ResizableTitle';
import { loadPageCache, savePageCache } from '../utils/pageCache';
import { formatTS } from '../utils/time';

const { RangePicker } = DatePicker;

const STATUS_COLOR = {
  assigned: 'blue', in_progress: 'orange', completed: 'green',
  rescore_requested: 'purple', canceled: 'default',
};
const STATUS_LABEL = {
  assigned: 'Draft', in_progress: 'In Progress', completed: 'Completed',
  rescore_requested: 'Disputed', canceled: 'Canceled',
};

const toOpts = (arr) => arr.map((v) => ({ label: v, value: v }));

export default function ErrorRawDataPage() {
  const navigate = useNavigate();

  const _cache = loadPageCache('error-raw-data');
  const [rows, setRows]           = useState(_cache?.rows ?? []);
  const [total, setTotal]         = useState(_cache?.total ?? 0);
  const [loading, setLoading]     = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage]           = useState(_cache?.page ?? 1);
  const PAGE_SIZE = 100;

  const [colWidths, setColWidths] = useState({});
  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  const [filterOpts, setFilterOpts] = useState({ campaigns: [], agents: [], forms: [], submitted_bys: [] });

  const _defaultStart = dayjs().startOf('month').format('YYYY-MM-DD');
  const _defaultEnd   = dayjs().format('YYYY-MM-DD');

  const [filters, setFilters] = useState(_cache?.filters ?? {
    start_date:       _defaultStart,
    end_date:         _defaultEnd,
    campaign:         '',
    agent_name:       '',
    form_name:        '',
    status:           '',
    section_contains: '',
    question_contains:'',
    error_count_min:  '',
    error_count_max:  '',
  });

  const datePickerValue = (filters.start_date && filters.end_date)
    ? [dayjs(filters.start_date), dayjs(filters.end_date)]
    : null;

  const committedRef = useRef(_cache?.filters ?? filters);

  useEffect(() => {
    api.get('/api/errors/raw-data/filter-options').then((r) => setFilterOpts(r.data)).catch(() => {});
  }, []);

  const _agentParams = (f) => {
    const out = {};
    if (f.agent_name != null && f.agent_name !== '') {
      if (typeof f.agent_name === 'number') out.agent_user_id = f.agent_name;
      else out.agent_name = f.agent_name;
    }
    return out;
  };

  const fetchData = useCallback((committed, pg) => {
    setLoading(true);
    const { agent_name: _a, ...rest } = committed;
    const params = { ...rest, ..._agentParams(committed), page: pg, page_size: PAGE_SIZE };
    Object.keys(params).forEach((k) => { if (!params[k] && params[k] !== 0) delete params[k]; });
    api.get('/api/errors/raw-data', { params })
      .then((r) => {
        setRows(r.data.rows);
        setTotal(r.data.total);
        savePageCache('error-raw-data', { rows: r.data.rows, total: r.data.total, page: pg, filters: committed });
      })
      .catch(() => message.error('Failed to load raw data.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = () => {
    committedRef.current = filters;
    setPage(1);
    fetchData(filters, 1);
  };

  const handlePageChange = (pg) => {
    setPage(pg);
    fetchData(committedRef.current, pg);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { agent_name: _a2, ...restExp } = committedRef.current;
      const params = { ...restExp, ..._agentParams(committedRef.current), export: 1 };
      Object.keys(params).forEach((k) => { if (!params[k] && params[k] !== 0) delete params[k]; });
      const res = await api.get('/api/errors/raw-data', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `error_raw_data_${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const setF = (key) => (val) => setFilters((prev) => ({ ...prev, [key]: val ?? '' }));

  const columns = [
    {
      title: 'Error ID',
      dataIndex: 'ErrorID',
      key: 'ErrorID',
      width: 80,
      render: (v) => (
        <Typography.Link onClick={() => navigate(`/errors/${v}`)}>
          {v}
        </Typography.Link>
      ),
    },
    {
      title: 'External Ref',
      dataIndex: 'ExternalRef',
      key: 'ExternalRef',
      width: 120,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Contact ID',
      dataIndex: 'ContactID',
      key: 'ContactID',
      width: 100,
      render: (v) => v ?? '—',
    },
    {
      title: 'Created Date',
      dataIndex: 'CreateDate',
      key: 'CreateDate',
      width: 110,
      render: (v) => v ?? '—',
    },
    {
      title: 'Date of Error',
      dataIndex: 'DateOfError',
      key: 'DateOfError',
      width: 110,
      render: (v) => v ?? '—',
    },
    {
      title: 'Agent Name',
      dataIndex: 'AgentName',
      key: 'AgentName',
      width: 130,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Campaign',
      dataIndex: 'Campaign',
      key: 'Campaign',
      width: 130,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      width: 110,
      render: (v) => v
        ? <Tag color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Tag>
        : '—',
    },
    {
      title: 'Acknowledged At',
      dataIndex: 'AcknowledgedAt',
      key: 'AcknowledgedAt',
      width: 160,
      render: (v) => v ? formatTS(v) : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Submitted By',
      dataIndex: 'SubmittedBy',
      key: 'SubmittedBy',
      width: 130,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Assigned To',
      dataIndex: 'AssignedTo',
      key: 'AssignedTo',
      width: 130,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Form',
      dataIndex: 'FormName',
      key: 'FormName',
      width: 160,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Section',
      dataIndex: 'SectionName',
      key: 'SectionName',
      width: 140,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Question',
      dataIndex: 'QuestionText',
      key: 'QuestionText',
      width: 220,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Question Type',
      dataIndex: 'QuestionType',
      key: 'QuestionType',
      width: 120,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
    {
      title: 'Error Count',
      dataIndex: 'ErrorCount',
      key: 'ErrorCount',
      width: 95,
      align: 'right',
      render: (v) => v > 0 ? <Tag color="red" style={{ margin: 0 }}>{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
    },
    {
      title: 'Feedback',
      dataIndex: 'FeedbackOnly',
      key: 'FeedbackOnly',
      width: 85,
      align: 'center',
      render: (v) => v ? <Tag color="orange" style={{ margin: 0 }}>Yes</Tag> : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Text Answer',
      dataIndex: 'TextAnswer',
      key: 'TextAnswer',
      width: 200,
      ellipsis: true,
      render: (v) => v ?? '—',
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Error Logs — Raw Data</Typography.Title>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exporting}
          disabled={total === 0}
        >
          Export CSV
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker
          placeholder={['Date From', 'Date To']}
          value={datePickerValue}
          onChange={(_, strs) =>
            setFilters((prev) => ({ ...prev, start_date: strs[0] || '', end_date: strs[1] || '' }))
          }
        />
        <Select
          placeholder="Campaign"
          style={{ width: 160 }}
          options={filterOpts.campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
          value={filters.campaign || undefined}
          onChange={setF('campaign')}
          allowClear
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder="Agent Name"
          style={{ width: 160 }}
          options={filterOpts.agents.map((a) => ({ label: a.name, value: a.id ?? a.name }))}
          value={filters.agent_name || undefined}
          onChange={setF('agent_name')}
          allowClear
          showSearch
        />
        <Select
          placeholder="Form"
          style={{ width: 180 }}
          options={toOpts(filterOpts.forms)}
          value={filters.form_name || undefined}
          onChange={setF('form_name')}
          allowClear
          showSearch
          optionFilterProp="label"
        />
        <Select
          placeholder="Status"
          style={{ width: 140 }}
          value={filters.status || undefined}
          onChange={setF('status')}
          allowClear
          options={[
            { label: 'Draft',       value: 'assigned'          },
            { label: 'Completed',   value: 'completed'         },
            { label: 'Disputed',    value: 'rescore_requested' },
            { label: 'Canceled',    value: 'canceled'          },
          ]}
        />
        <Input
          placeholder="Section contains…"
          style={{ width: 170 }}
          value={filters.section_contains}
          onChange={(e) => setF('section_contains')(e.target.value)}
          allowClear
        />
        <Input
          placeholder="Question contains…"
          style={{ width: 190 }}
          value={filters.question_contains}
          onChange={(e) => setF('question_contains')(e.target.value)}
          allowClear
        />
        <Space.Compact>
          <InputNumber
            placeholder="Err min"
            style={{ width: 85 }}
            value={filters.error_count_min === '' ? null : Number(filters.error_count_min)}
            onChange={(v) => setF('error_count_min')(v == null ? '' : String(v))}
            min={0}
          />
          <InputNumber
            placeholder="Err max"
            style={{ width: 85 }}
            value={filters.error_count_max === '' ? null : Number(filters.error_count_max)}
            onChange={(v) => setF('error_count_max')(v == null ? '' : String(v))}
            min={0}
          />
        </Space.Compact>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          Search
        </Button>
      </Space>

      <Table
        rowKey={(r, i) => `${r.ErrorID}-${r.SectionOrder}-${r.QuestionOrder}-${i}`}
        dataSource={rows}
        columns={columns.map(toResizable)}
        components={{ header: { cell: ResizableTitle } }}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: handlePageChange,
          showTotal: (t) => `${t.toLocaleString()} rows`,
          showSizeChanger: false,
        }}
        locale={{ emptyText: 'Use the filters above and click Search to load data.' }}
      />
    </>
  );
}
